import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { LOCATIONS } from '../../../constants';
import { REENTRY_PLANNING_TABS } from './constants/reentry-planning.constants';
import { useLocationsRevision } from './hooks/useLocationsRevision';
import {
  canAccessReentryPlanning,
  resolveEntitledPlanTypesFromLocations,
  shouldShowCreatePlanButton,
} from './utils/reentry-planning.util';

export const ACCESS_STATUS = {
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
};

export const useReentryPlanningModuleHook = () => {
  const [global] = useGlobal();
  const [accessStatus, setAccessStatus] = useState(ACCESS_STATUS.PENDING);
  const [activeTab, setActiveTab] = useState(REENTRY_PLANNING_TABS.ALL_PLANS);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const locationsRevision = useLocationsRevision();

  const entitledPlanTypes = useMemo(
    () => resolveEntitledPlanTypesFromLocations(LOCATIONS),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationsRevision],
  );

  const showCreatePlanButton = shouldShowCreatePlanButton(entitledPlanTypes);

  const tabs = [
    {
      label: REENTRY_PLANNING_TABS.ALL_PLANS,
      value: REENTRY_PLANNING_TABS.ALL_PLANS,
      Icon: MeetingRoomIcon,
    },
  ];

  const checkAccess = useCallback(() => {
    const granted = canAccessReentryPlanning(global.permissions, global.permissionIds, {
      isSuperAdmin: global.isSuperAdmin,
      locations: LOCATIONS,
    });

    setAccessStatus(granted ? ACCESS_STATUS.GRANTED : ACCESS_STATUS.DENIED);
  }, [global.permissions, global.permissionIds, global.isSuperAdmin]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const onTabChange = useCallback((_event, value) => {
    setActiveTab(value);
  }, []);

  const refreshPlans = useCallback(() => {
    setLastRefreshed(new Date());
  }, []);

  return {
    accessStatus,
    activeTab,
    entitledPlanTypes,
    lastRefreshed,
    onTabChange,
    refreshPlans,
    tabs,
    showCreatePlanButton,
  };
};
